import { useState } from 'react';
import { Share2, Heart, Check } from 'lucide-react';
import { DictionaryEntry } from '../types';
import { config } from '../data/config';
import { supabase } from '../lib/supabase';


interface DefinitionFooterProps {
    entry: DictionaryEntry;
    variant?: 'default' | 'cyberpunk';
    accentColor?: string; // Classe Tailwind pour la couleur d'accent (ex: "text-cyan-400")
}

export const DefinitionFooter = ({ entry, variant = 'default', accentColor = 'text-indigo-600' }: DefinitionFooterProps) => {
    const [likes, setLikes] = useState(entry.likes || 0);
    const [hasLiked, setHasLiked] = useState(false);
    const [showCopied, setShowCopied] = useState(false);

    const isCyberpunk = variant === 'cyberpunk';

    const handleShare = async () => {
        const url = `${window.location.origin}/definition/${encodeURIComponent(entry.term.toLowerCase())}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${entry.term} - ${config.site.title}`,
                    text: entry.definition,
                    url: url
                });
            } catch (err) {
                console.error('Error sharing:', err);
            }
        } else {
            // Fallback: copy to clipboard
            try {
                await navigator.clipboard.writeText(url);
                setShowCopied(true);
                setTimeout(() => setShowCopied(false), 2000);
            } catch (err) {
                console.error('Error copying to clipboard', err);
            }
        }
    };

    const handleLike = async () => {
        if (hasLiked) return; // Prevent multiple likes in same session

        // Optimistic update
        setLikes(prev => prev + 1);
        setHasLiked(true);

        // Call Supabase RPC to increment likes safely
        // We assume an RPC function 'increment_likes' exists or we do a direct update
        // Ideally use RPC: create function increment_likes(row_id int) returns void as $$ update dictionary_entries set likes = likes + 1 where id = row_id; $$ language sql;

        // For now, simpler approach: direct update (race condition possible but acceptable for this scale)
        const { error } = await supabase.rpc('increment_likes', { row_id: entry.id });

        if (error) {
            console.error('Error incrementing likes:', error);
            // Rollback on error? Maybe not needed for simple like counter
            // If RPC doesnt exist, try direct update:
            if (error.message.includes('function') && error.message.includes('does not exist')) {
                const { error: updateError } = await supabase
                    .from('dictionary_entries')
                    .update({ likes: likes + 1 })
                    .eq('id', entry.id);

                if (updateError) {
                    console.error('Error manual update likes:', updateError);
                }
            }
        }
    };

    // Styles dynamiques
    const wrapperClass = isCyberpunk
        ? "mt-8 pt-6 border-t border-slate-800/50 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500 font-mono"
        : "mt-8 pt-6 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-400";

    const shareButtonClass = isCyberpunk
        ? `flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900/50 hover:bg-slate-800 text-slate-400 hover:${accentColor} border border-slate-800 transition-all active:scale-95 group`
        : `flex items-center gap-2 px-4 py-2 rounded-full bg-slate-50 hover:bg-indigo-50 text-slate-500 hover:text-indigo-600 transition-all active:scale-95 group`;

    const likeButtonClass = isCyberpunk
        ? `flex items-center gap-2 px-4 py-2 rounded-lg transition-all active:scale-95 border ${hasLiked ? `bg-slate-800 ${accentColor} border-slate-600` : `bg-slate-900/50 border-slate-800 text-slate-400 hover:${accentColor}`}`
        : `flex items-center gap-2 px-4 py-2 rounded-full transition-all active:scale-95 border ${hasLiked ? 'bg-rose-50 border-rose-100 text-rose-500' : 'bg-white border-slate-200 text-slate-400 hover:border-rose-200 hover:text-rose-500'}`;

    return (
        <div className="mt-8">
            {/* Callout Livre */}
            <div className={isCyberpunk ? "mb-6 p-4 rounded-xl bg-cyan-950/30 border border-cyan-500/20 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs" : "mb-6 p-4 rounded-2xl bg-amber-50/60 border border-amber-200/60 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs"}>
                <span className={isCyberpunk ? "text-slate-300 font-mono" : "text-slate-700 font-medium"}>
                    📚 Retrouvez les 200 définitions complètes dans le livre (Ebook gratuit ou Papier)
                </span>
                <a
                    href="https://brou.pm/book_dico_pm"
                    className={isCyberpunk ? "px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-mono font-bold whitespace-nowrap transition-colors" : "px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-sans font-bold whitespace-nowrap transition-colors shadow-sm"}
                >
                    Obtenir le livre →
                </a>
            </div>

            <div className={wrapperClass}>
                {/* Credits */}
                <div className="flex flex-col md:flex-row items-center gap-1 md:gap-4 font-sans text-center md:text-left">
                    <span>Par {config.site.author}</span>
                    <span className="hidden md:inline">•</span>
                    <span>{config.site.collection}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3">
                    {/* Share Button */}
                    <button
                        onClick={handleShare}
                        className={shareButtonClass}
                        title="Partager la définition"
                    >
                        {showCopied ? <Check size={18} className="text-green-500" /> : <Share2 size={18} />}
                        <span className="font-medium">{showCopied ? 'Lien copié !' : 'Partager'}</span>
                    </button>

                    {/* Like Button */}
                    <button
                        onClick={handleLike}
                        disabled={hasLiked}
                        className={likeButtonClass}
                        title="J'aime cette définition"
                    >
                        <Heart size={18} className={hasLiked ? "fill-current" : ""} />
                        <span className="font-medium">{likes}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
